// Client/src/pages/digi/Invoice/TemplateDesigner/canvas/Canvas.jsx
//
// The page you design on. The server-rendered invoice sits in an iframe (no
// scripts; same-origin so we can measure it) and a transparent overlay on top
// handles everything you do with the pointer:
//   - click selects (an element, or a section by clicking its empty space or label)
//   - drag moves: free elements to exact mm positions with snapping guides
//     (hold Alt to switch snapping off), flowing elements to a new place in the
//     stack, into a column, or into another section
//   - handles resize free elements (Shift keeps the ratio), the right-edge
//     handle sets a flowing element's width, the bottom handle a free section's
//     height
//   - double-click a text to edit it in place
//   - drop tiles from the Elements panel or fields from the Fields panel
// While dragging, the element inside the iframe is moved directly, so what you
// see is the real rendering; the design is updated on release.

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import FeatherIcon from "feather-icons-react";
import useRenderedFrames from "./useRenderedFrames";
import TextPopover from "./TextPopover";
import {
  PX_PER_MM,
  contentSize,
  findBand,
  locate,
  makeElement,
  insertElement,
  moveElement,
  updateElement,
  updateBand,
  siblingFrames,
  placementError,
  elementLabel,
  addBand,
} from "../designModel";
import { snapTargets, snapMove, resizeFrame, insertionIndex, HANDLES, HANDLE_CURSORS, round1, clamp } from "../geometry";
import { DRAG_MIME } from "../rail/ElementsPanel";

const GUTTER = 120; // px either side of the page, for section labels
const DRAG_THRESHOLD = 4; // px before a press becomes a drag
const SNAP_PX = 6;

export default function Canvas({ templateId, design, sample, zoom, selection, onSelect, change, readOnly, ctx, onRenderState }) {
  const stageRef = useRef(null);
  const interaction = useRef(null);
  const [visual, setVisual] = useState(null); // live drag feedback
  const [hover, setHover] = useState(null);
  const [popover, setPopover] = useState(null);
  const [addMenu, setAddMenu] = useState(null); // band index to insert after

  const { slots, active, frames, onFrameLoad, layout, rendering, error, activeDoc } = useRenderedFrames({
    templateId,
    design,
    sample,
  });

  useEffect(() => {
    onRenderState?.({ rendering, error });
  }, [rendering, error, onRenderState]);

  const page = design.page;
  const cs = contentSize(page);
  const stageW = Math.round(cs.pageW * PX_PER_MM + 2 * GUTTER);
  const stageH = Math.max(layout?.height || cs.pageH * PX_PER_MM + 48, 300);

  const elRect = useCallback((id) => layout?.elements.find((e) => e.id === id) || null, [layout]);
  const bandRect = useCallback((id) => layout?.bands.find((b) => b.id === id) || null, [layout]);

  const point = (e) => {
    const r = stageRef.current.getBoundingClientRect();
    return { x: (e.clientX - r.left) / zoom, y: (e.clientY - r.top) / zoom };
  };

  // What is under a stage point, using the iframe's own hit testing (z-order exact).
  const hit = (x, y) => {
    const doc = activeDoc();
    const node = doc?.elementFromPoint(x, y);
    const elNode = node?.closest?.("[data-el-id]");
    const bandNode = node?.closest?.("[data-band-id]");
    let bandId = bandNode?.getAttribute("data-band-id") || null;
    if (!bandId && layout) {
      // Between sections or in the gutter: the nearest section by height.
      const b = layout.bands.find((r) => y >= r.y && y <= r.y + r.h);
      bandId = b?.id || null;
    }
    return { elId: elNode?.getAttribute("data-el-id") || null, bandId };
  };

  // Where a dragged/dropped element would land.
  const dropTarget = (x, y, dragged) => {
    if (!layout) return null;
    let b = layout.bands.find((r) => y >= r.y && y <= r.y + r.h);
    if (!b) {
      // Nearest section above/below the pointer.
      b = [...layout.bands].sort((p, q) => Math.abs(p.y + p.h / 2 - y) - Math.abs(q.y + q.h / 2 - y))[0];
    }
    if (!b) return null;
    const band = findBand(design, b.id);
    if (!band) return null;
    if (band.kind === "free") {
      const w = dragged?.size?.w ?? 40;
      const h = dragged?.size?.h ?? 10;
      const gx = dragged?.grab?.x ?? 0;
      const gy = dragged?.grab?.y ?? 0;
      const fx = round1((x - b.x) / PX_PER_MM - gx);
      const fy = round1((y - b.y) / PX_PER_MM - gy);
      return { bandId: band.id, free: true, frame: { x: clamp(fx, 0, cs.w - 1), y: clamp(fy, 0, Math.max(0, band.height - 1)), w, h }, bandRect: b };
    }
    // Flowing: inside a column if over one (rows can't nest), else the section.
    const col = dragged?.type !== "row" ? layout.cols.find((c) => c.rowId !== dragged?.id && x >= c.x && x <= c.x + c.w && y >= c.y && y <= c.y + c.h) : null;
    const colInBand = col && layout.elements.find((e) => e.id === col.rowId)?.bandId === band.id ? col : null;
    const container = colInBand || b;
    const kids = layout.elements
      .filter((e) => e.bandId === band.id && (colInBand ? e.colId === colInBand.id : e.colId === null))
      .filter((e) => {
        // direct children only: skip elements nested inside a row column
        if (colInBand) return true;
        const loc = locate(design, e.id);
        return loc && !loc.col;
      })
      .sort((p, q) => p.y - q.y);
    const index = insertionIndex(kids.map((k) => ({ top: k.y, height: k.h })), y);
    const lineY = index < kids.length ? kids[index].y - 2 : kids.length ? kids[kids.length - 1].y + kids[kids.length - 1].h + 2 : container.y + 4;
    return {
      bandId: band.id,
      colId: colInBand?.id,
      index,
      free: false,
      line: { x: container.x, y: lineY, w: container.w },
      bandRect: b,
    };
  };

  // ------------------------------------------------------------ pointer

  const liveNode = (id) => activeDoc()?.querySelector(`[data-el-id="${id}"]`);

  const onPointerDown = (e) => {
    if (readOnly || e.button !== 0) return;
    setAddMenu(null);
    const p = point(e);
    const handle = e.target.closest?.("[data-handle]")?.getAttribute("data-handle");
    stageRef.current.setPointerCapture?.(e.pointerId);

    if (handle && selection) {
      if (handle === "band-s") {
        const band = findBand(design, selection.id) || locate(design, selection.id)?.band;
        if (band) interaction.current = { kind: "band-height", band, start: p, started: true };
        return;
      }
      const loc = locate(design, selection.id);
      if (!loc) return;
      if (handle === "flow-e") {
        const r = elRect(loc.el.id);
        const container = loc.col ? layout.cols.find((c) => c.id === loc.col.id) : bandRect(loc.band.id);
        interaction.current = { kind: "flow-width", loc, start: p, startPct: loc.el.widthPct ?? 100, containerW: container?.w || r.w, started: true };
      } else {
        interaction.current = { kind: "resize", loc, handle, start: p, startFrame: { ...loc.el.frame }, started: true };
      }
      return;
    }

    const h = hit(p.x, p.y);
    if (h.elId) {
      const loc = locate(design, h.elId);
      onSelect({ kind: "element", id: h.elId });
      if (loc && !loc.el.locked) {
        const r = elRect(h.elId);
        interaction.current = {
          kind: loc.el.frame ? "move-free" : "move-flow",
          loc,
          start: p,
          started: false,
          startFrame: loc.el.frame ? { ...loc.el.frame } : null,
          rect: r,
          grab: r ? { x: (p.x - r.x) / PX_PER_MM, y: (p.y - r.y) / PX_PER_MM } : { x: 0, y: 0 },
        };
      }
    } else if (h.bandId) {
      onSelect({ kind: "band", id: h.bandId });
    } else {
      onSelect(null);
    }
  };

  const onPointerMove = (e) => {
    const p = point(e);
    const it = interaction.current;
    if (!it) {
      if (!readOnly) {
        const h = hit(p.x, p.y);
        setHover(h.elId || (h.bandId ? `band:${h.bandId}` : null));
      }
      return;
    }
    const dx = p.x - it.start.x;
    const dy = p.y - it.start.y;
    if (!it.started) {
      if (Math.hypot(dx, dy) < DRAG_THRESHOLD) return;
      it.started = true;
    }
    const dxMm = dx / PX_PER_MM;
    const dyMm = dy / PX_PER_MM;
    const threshold = SNAP_PX / zoom / PX_PER_MM;

    if (it.kind === "band-height") {
      const hNew = clamp(round1(it.band.height + dyMm), 5, 250);
      const node = activeDoc()?.querySelector(`[data-band-id="${it.band.id}"]`);
      if (node) node.style.height = `${hNew}mm`;
      it.value = hNew;
      setVisual({ kind: "band-height", bandId: it.band.id, label: `${hNew} mm` });
      return;
    }

    if (it.kind === "flow-width") {
      const pct = clamp(Math.round(it.startPct + (dx / it.containerW) * 100), 5, 100);
      const node = liveNode(it.loc.el.id);
      if (node) node.style.width = `${pct}%`;
      it.value = pct;
      setVisual({ kind: "flow-width", label: `${pct}%` });
      return;
    }

    if (it.kind === "resize") {
      const band = it.loc.band;
      const targets = e.altKey ? null : snapTargets(cs.w, band.height, siblingFrames(band, it.loc.el.id));
      const { frame, guides } = resizeFrame(it.startFrame, it.handle, dxMm, dyMm, { keepRatio: e.shiftKey, targets, threshold });
      applyFrame(it.loc.el.id, frame);
      it.value = frame;
      setVisual({ kind: "resize", bandId: band.id, guides, frame, label: `${frame.w} × ${frame.h} mm` });
      return;
    }

    if (it.kind === "move-free") {
      const origin = bandRect(it.loc.band.id);
      const overOrigin = origin && p.y >= origin.y - 8 && p.y <= origin.y + origin.h + 8;
      if (overOrigin) {
        const raw = { ...it.startFrame, x: it.startFrame.x + dxMm, y: it.startFrame.y + dyMm };
        const targets = snapTargets(cs.w, it.loc.band.height, siblingFrames(it.loc.band, it.loc.el.id));
        const { frame, guides } = e.altKey ? { frame: { ...raw, x: round1(raw.x), y: round1(raw.y) }, guides: {} } : snapMove(raw, targets, threshold);
        applyFrame(it.loc.el.id, frame);
        it.value = { same: true, frame };
        setVisual({ kind: "move-free", bandId: it.loc.band.id, guides, frame, label: `${frame.x}, ${frame.y} mm` });
      } else {
        // Leaving the section: put the element back and show where it would go.
        applyFrame(it.loc.el.id, it.startFrame);
        const target = dropTarget(p.x, p.y, { type: it.loc.el.type, id: it.loc.el.id, size: { w: it.startFrame.w, h: it.startFrame.h }, grab: it.grab });
        it.value = { same: false, target };
        setVisual({ kind: "move-away", target, ghost: ghostRect(it, p) });
      }
      return;
    }

    if (it.kind === "move-flow") {
      const size = it.rect ? { w: round1(it.rect.w / PX_PER_MM), h: round1(it.rect.h / PX_PER_MM) } : undefined;
      const target = dropTarget(p.x, p.y, { type: it.loc.el.type, id: it.loc.el.id, size, grab: it.grab });
      it.value = { target };
      setVisual({ kind: "move-away", target, ghost: ghostRect(it, p) });
    }
  };

  const ghostRect = (it, p) => (it.rect ? { x: p.x - it.grab.x * PX_PER_MM, y: p.y - it.grab.y * PX_PER_MM, w: it.rect.w, h: it.rect.h } : null);

  const applyFrame = (id, f) => {
    const node = liveNode(id);
    if (!node) return;
    node.style.left = `${f.x}mm`;
    node.style.top = `${f.y}mm`;
    node.style.width = `${f.w}mm`;
    node.style.height = `${f.h}mm`;
  };

  const onPointerUp = (e) => {
    const it = interaction.current;
    interaction.current = null;
    stageRef.current?.releasePointerCapture?.(e.pointerId);
    setVisual(null);
    if (!it || !it.started || it.value === undefined) return;
    if (it.kind === "band-height") {
      change((d) => updateBand(d, it.band.id, { height: it.value }));
    } else if (it.kind === "flow-width") {
      change((d) => updateElement(d, it.loc.el.id, { widthPct: it.value }));
    } else if (it.kind === "resize") {
      change((d) => updateElement(d, it.loc.el.id, { frame: it.value }));
    } else if (it.kind === "move-free" && it.value.same) {
      change((d) => updateElement(d, it.loc.el.id, { frame: { ...it.value.frame } }));
    } else if (it.value.target) {
      const t = it.value.target;
      if (placementError(design, it.loc.el.type, t)) return;
      change((d) => moveElement(d, it.loc.el.id, t.free ? { bandId: t.bandId, frame: t.frame } : { bandId: t.bandId, colId: t.colId, index: t.index }));
    }
  };

  const onDoubleClick = (e) => {
    if (readOnly) return;
    const p = point(e);
    const h = hit(p.x, p.y);
    const loc = h.elId && locate(design, h.elId);
    if (loc && loc.el.type === "text") setPopover(h.elId);
  };

  // ------------------------------------------------------------ drop from rails

  const dragKind = (e) => {
    const types = Array.from(e.dataTransfer?.types || []);
    if (types.includes(DRAG_MIME)) return "element";
    if (types.includes("application/x-td-field")) return "field";
    return null;
  };

  const onDragOver = (e) => {
    if (readOnly || !dragKind(e)) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = "copy";
    const p = point(e);
    setVisual({ kind: "drop", target: dropTarget(p.x, p.y, { size: { w: 40, h: 10 } }) });
  };

  const onDrop = (e) => {
    const kind = dragKind(e);
    if (!kind) return;
    e.preventDefault();
    setVisual(null);
    const p = point(e);
    const target = dropTarget(p.x, p.y, { size: { w: 40, h: 10 } });
    if (!target) return;
    let el;
    if (kind === "element") {
      let data;
      try {
        data = JSON.parse(e.dataTransfer.getData(DRAG_MIME));
      } catch {
        return;
      }
      el = makeElement(data.type, target.free, { ...(data.opts || {}), at: target.frame });
    } else {
      const key = e.dataTransfer.getData("application/x-td-field");
      el = makeElement("text", target.free, { runs: [{ field: key }], at: target.frame });
    }
    const err = placementError(design, el.type, target);
    if (err) {
      ctx.notify?.(err);
      return;
    }
    change((d) => insertElement(d, el, target.free ? { bandId: target.bandId, frame: el.frame } : { bandId: target.bandId, colId: target.colId, index: target.index }));
    onSelect({ kind: "element", id: el.id });
  };

  // ------------------------------------------------------------ overlay bits

  // A free element's box from its frame (mm) inside its section's measured box.
  const frameRect = (bandId, f) => {
    const b = bandRect(bandId);
    return b && f ? { x: b.x + f.x * PX_PER_MM, y: b.y + f.y * PX_PER_MM, w: f.w * PX_PER_MM, h: f.h * PX_PER_MM } : null;
  };

  const sel = selection?.kind === "element" ? locate(design, selection.id) : null;
  // Free elements are drawn from the design (not the last render), so the box
  // stays put right after a drop while the page re-renders.
  const selRect = sel ? (sel.el.frame && !sel.col ? frameRect(sel.band.id, sel.el.frame) : elRect(sel.el.id)) : null;
  const selBandId = selection?.kind === "band" ? selection.id : sel?.band.id;
  const shownSelRect =
    visual && (visual.kind === "resize" || visual.kind === "move-free") && sel ? frameRect(sel.band.id, visual.frame) : selRect;

  const pageBreaks = useMemo(() => {
    if (!layout?.page) return [];
    const contentPx = cs.h * PX_PER_MM;
    const top = layout.page.y + page.margins.top * PX_PER_MM;
    const out = [];
    for (let y = top + contentPx, n = 2; y < layout.height - 10 && n < 30; y += contentPx, n++) out.push({ y, n });
    return out;
  }, [layout, cs.h, page.margins.top]);

  const hoverRect = hover && !hover.startsWith("band:") && hover !== selection?.id ? elRect(hover) : null;
  const hoverBand = hover?.startsWith("band:") ? hover.slice(5) : sel ? null : hoverRect ? layout?.elements.find((x) => x.id === hover)?.bandId : null;

  const popoverEl = popover ? locate(design, popover)?.el : null;
  const popoverRect = popoverEl ? elRect(popover) : null;

  return (
    <div className="td-canvas__sizer" style={{ width: stageW * zoom, height: stageH * zoom }}>
      <div
        ref={stageRef}
        className={`td-stage${readOnly ? " is-readonly" : ""}`}
        style={{ width: stageW, height: stageH, transform: `scale(${zoom})` }}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        onPointerLeave={() => !interaction.current && setHover(null)}
        onDoubleClick={onDoubleClick}
        onDragOver={onDragOver}
        onDragLeave={() => setVisual((v) => (v?.kind === "drop" ? null : v))}
        onDrop={onDrop}
      >
        {slots.map((s, i) => (
          <iframe
            // Remount per render so identical HTML (e.g. after undo) still fires onLoad.
            key={`${i}-${s.n}`}
            ref={frames[i]}
            title={i === active ? "Invoice preview" : "Invoice preview (loading)"}
            className={`td-frame${i === active ? " is-active" : ""}`}
            sandbox="allow-same-origin"
            srcDoc={s.html}
            style={{ width: stageW, height: stageH }}
            onLoad={() => onFrameLoad(i)}
            aria-hidden={i !== active}
            tabIndex={-1}
          />
        ))}

        {!readOnly && layout && (
          <div className="td-overlay" aria-hidden="true">
            {pageBreaks.map((b) => (
              <div key={b.n} className="td-ov-break" style={{ top: b.y, left: GUTTER - 8, width: stageW - 2 * GUTTER + 16 }}>
                <span>Page {b.n}</span>
              </div>
            ))}

            {layout.bands.map((b, i) => {
              const band = findBand(design, b.id);
              if (!band) return null;
              const isSel = selBandId === b.id;
              const isHover = hoverBand === b.id;
              return (
                <React.Fragment key={b.id}>
                  <div className={`td-ov-band${isSel ? " is-selected" : ""}${isHover ? " is-hover" : ""}${band.kind === "free" ? " is-free" : ""}`} style={{ left: b.x, top: b.y, width: b.w, height: b.h }} />
                  <div
                    className={`td-ov-bandlabel${isSel ? " is-selected" : ""}`}
                    style={{ left: 8, top: b.y, width: GUTTER - 20 }}
                    data-band-label={b.id}
                    onPointerDown={(e) => {
                      e.stopPropagation();
                      onSelect({ kind: "band", id: b.id });
                    }}
                  >
                    <FeatherIcon icon={band.kind === "free" ? "crop" : "align-justify"} size={11} />
                    <span>{band.label}</span>
                    {band.pin !== "none" && <FeatherIcon icon="repeat" size={11} />}
                  </div>
                  <button
                    type="button"
                    className="td-ov-addband"
                    style={{ left: 8, top: b.y + b.h - 10 }}
                    title="Add a section below"
                    onPointerDown={(e) => {
                      e.stopPropagation();
                      setAddMenu(addMenu === i ? null : i);
                    }}
                  >
                    <FeatherIcon icon="plus" size={11} />
                  </button>
                  {addMenu === i && (
                    <div className="td-ov-addmenu" style={{ left: 8, top: b.y + b.h + 12 }} onPointerDown={(e) => e.stopPropagation()}>
                      {["free", "flow"].map((k) => (
                        <button
                          key={k}
                          type="button"
                          onClick={() => {
                            let newId = null;
                            change((d) => {
                              const r = addBand(d, k, i + 1);
                              newId = r.id;
                              return r.design;
                            });
                            setAddMenu(null);
                            if (newId) onSelect({ kind: "band", id: newId });
                          }}
                        >
                          {k === "free" ? "Free section" : "Flowing section"}
                        </button>
                      ))}
                    </div>
                  )}
                  {band.kind === "free" && isSel && (
                    <div className="td-ov-bandhandle" data-handle="band-s" style={{ left: b.x + b.w / 2 - 18, top: b.y + b.h - 4 }} title="Drag to change the section height" />
                  )}
                </React.Fragment>
              );
            })}

            {hoverRect && <div className="td-ov-hover" style={{ left: hoverRect.x, top: hoverRect.y, width: hoverRect.w, height: hoverRect.h }} />}

            {shownSelRect && sel && (
              <div className={`td-ov-select${sel.el.locked ? " is-locked" : ""}`} style={{ left: shownSelRect.x, top: shownSelRect.y, width: shownSelRect.w, height: shownSelRect.h }}>
                <span className="td-ov-tag">
                  {sel.el.locked && <FeatherIcon icon="lock" size={10} />} {elementLabel(sel.el)}
                </span>
                {!sel.el.locked && sel.el.frame &&
                  HANDLES.map((hd) => <div key={hd} className={`td-ov-handle td-ov-handle--${hd}`} data-handle={hd} style={{ cursor: HANDLE_CURSORS[hd] }} />)}
                {!sel.el.locked && !sel.el.frame && <div className="td-ov-handle td-ov-handle--e" data-handle="flow-e" style={{ cursor: "ew-resize" }} title="Drag to change the width" />}
              </div>
            )}

            {visual?.guides && visual.bandId && (() => {
              const b = bandRect(visual.bandId);
              if (!b) return null;
              return (
                <>
                  {visual.guides.x !== null && visual.guides.x !== undefined && <div className="td-ov-guide td-ov-guide--v" style={{ left: b.x + visual.guides.x * PX_PER_MM, top: b.y, height: b.h }} />}
                  {visual.guides.y !== null && visual.guides.y !== undefined && <div className="td-ov-guide td-ov-guide--h" style={{ top: b.y + visual.guides.y * PX_PER_MM, left: b.x, width: b.w }} />}
                </>
              );
            })()}

            {visual?.label && shownSelRect && (
              <div className="td-ov-measure" style={{ left: shownSelRect.x, top: shownSelRect.y + shownSelRect.h + 6 }}>
                {visual.label}
              </div>
            )}
            {visual?.kind === "band-height" && (() => {
              const b = bandRect(visual.bandId);
              return b ? <div className="td-ov-measure" style={{ left: b.x + b.w / 2 + 24, top: b.y + b.h - 10 }}>{visual.label}</div> : null;
            })()}

            {(visual?.kind === "move-away" || visual?.kind === "drop") && visual.target && (
              <>
                <div className="td-ov-band is-target" style={{ left: visual.target.bandRect.x, top: visual.target.bandRect.y, width: visual.target.bandRect.w, height: visual.target.bandRect.h }} />
                {visual.target.line && <div className="td-ov-insert" style={{ left: visual.target.line.x, top: visual.target.line.y, width: visual.target.line.w }} />}
                {visual.target.free && visual.kind === "drop" && (() => {
                  const r = frameRect(visual.target.bandId, visual.target.frame);
                  return r ? <div className="td-ov-ghost" style={{ left: r.x, top: r.y, width: r.w, height: r.h }} /> : null;
                })()}
              </>
            )}
            {visual?.ghost && <div className="td-ov-ghost" style={{ left: visual.ghost.x, top: visual.ghost.y, width: visual.ghost.w, height: visual.ghost.h }} />}
          </div>
        )}

        {!readOnly && layout && !design.bands.length && (
          <div className="td-ov-empty">
            <p>This page is empty.</p>
            <button
              type="button"
              className="td-btn td-btn--primary"
              onClick={() => {
                let id = null;
                change((d) => {
                  const r = addBand(d, "flow");
                  id = r.id;
                  return r.design;
                });
                if (id) onSelect({ kind: "band", id });
              }}
            >
              Add a section
            </button>
          </div>
        )}
      </div>

      {popoverEl && popoverRect && (
        <TextPopover
          el={popoverEl}
          ctx={ctx}
          style={{ left: popoverRect.x * zoom, top: (popoverRect.y + popoverRect.h) * zoom + 8 }}
          onChange={(runs, ck) => change((d) => updateElement(d, popoverEl.id, { runs }), { coalesce: ck || `pop-${popoverEl.id}` })}
          onClose={() => setPopover(null)}
        />
      )}
      {error && (
        <div className="td-canvas__error" role="alert">
          <FeatherIcon icon="alert-triangle" size={14} /> {error}
        </div>
      )}
    </div>
  );
}
